import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { profilePictureUrl, themeImageUrl } from '../utils/storageUrls';

// Newsletter Form Component
const NewsletterForm = () => {
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    const [subscribed, setSubscribed] = useState(false);

    const handleSubscribe = async (e) => {
        e.preventDefault();
        
        if (!email || !email.includes('@')) {
            setMessage('Please enter a valid email address.');
            return;
        }

        setIsSubmitting(true);
        setMessage('');

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/newsletter_subscribe.php`, {
                email: email
            });

            if (response.data.success) {
                setSubscribed(true);
                setEmail('');
                setMessage(response.data.message);
                setTimeout(() => {
                    setSubscribed(false);
                    setMessage('');
                }, 5000);
            } else {
                setMessage(response.data.message);
            }
        } catch (error) {
            console.error('Newsletter subscription error:', error);
            setMessage('An error occurred. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <form className="flex max-w-md mx-auto mb-4" onSubmit={handleSubscribe}>
                <input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 px-4 py-2 rounded-l-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                    disabled={isSubmitting}
                />
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-4 py-2 rounded-r-lg transition-colors ${
                        isSubmitting 
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                            : 'bg-red-600 text-white hover:bg-red-700 hover:text-white'
                    }`}
                >
                    {isSubmitting ? (
                        <div className="w-4 h-4 simple-spinner mx-auto" style={{ borderWidth: '2px', borderColor: '#4b5563', borderTopColor: 'transparent' }}></div>
                    ) : (
                        'Subscribe'
                    )}
                </button>
            </form>
            {(subscribed || message) && (
                <p className={`text-sm mb-2 ${
                    subscribed ? 'text-green-600' : 'text-red-600'
                }`}>
                    {message || 'Thanks for subscribing!'}
                </p>
            )}
        </div>
    );
};

const FAQ_ITEMS = [
    {
        q: 'Is MyBrickLog free to use?',
        a: 'Yes, MyBrickLog is completely free. There are no subscription plans or premium tiers. Creating an account and tracking your entire collection costs absolutely nothing.'
    },
    {
        q: 'How many LEGO® sets are in the database?',
        a: 'MyBrickLog contains over 25,000 LEGO® sets spanning every theme ever produced, from the earliest sets of the 1950s through the latest current-year releases. The database is updated regularly as new sets are announced and released.'
    },
    {
        q: 'Does MyBrickLog show prices for retired LEGO® sets?',
        a: 'Yes. For sets that are no longer available at retail (retired sets), MyBrickLog displays current sealed and used aftermarket market prices so you can see how much a set is worth today. Retail prices are also shown for all sets that were sold in stores.'
    },
    {
        q: 'Can I track minifigures in my collection?',
        a: 'Yes. For every set in your collection, you can view the complete list of included minifigures and mark which ones you personally own. This is especially useful for collectors who buy sets specifically for their minifigures or who trade figures with other collectors.'
    },
    {
        q: 'Can I track multiple copies of the same set?',
        a: 'Absolutely. You can set a quantity for each set in your collection and individually mark each copy as sealed, opened, or complete. This is perfect for collectors who own sealed investment copies alongside opened display copies of the same set.'
    },
    {
        q: 'Can I make my collection private?',
        a: 'By default your collection is public so others can discover and browse it. You can make your collection private in your account settings if you prefer to keep it just for personal tracking.'
    },
    {
        q: 'What LEGO® themes does MyBrickLog cover?',
        a: 'MyBrickLog covers all LEGO® themes including Star Wars, Technic, City, Creator Expert, Harry Potter, Ninjago, Architecture, Ideas, Marvel, DC, Icons, and dozens more; plus older discontinued themes like Pirates, Castle, Aquazone, Adventurers, Bionicle, and many others.'
    },
    {
        q: 'Can I share my wishlist with family for gift ideas?',
        a: "Yes. Your wishlist is accessible via a public link that you can share with family or friends before birthdays and holidays. They can see exactly which LEGO® sets you want, helping them pick the perfect gift without the risk of buying a set you already own."
    },
];

const FaqSection = () => {
    const [openIndex, setOpenIndex] = React.useState(null);

    const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

    return (
        <section className="py-16 bg-white border-t border-gray-100">
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
                <div className="text-center mb-10">
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Frequently Asked Questions</h2>
                    <p className="text-gray-600">Everything you need to know about tracking your LEGO® collection with MyBrickLog.</p>
                </div>
                <div className="space-y-3">
                    {FAQ_ITEMS.map((item, i) => {
                        const isOpen = openIndex === i;
                        return (
                        <div
                            className={`rounded-xl border transition-shadow ${
                                isOpen
                                    ? 'border-red-700 shadow-md shadow-red-900/10'
                                    : 'border-gray-200'
                            }`}
                        >
                            <button
                                type="button"
                                className={`w-full text-left px-6 py-4 flex justify-between items-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset ${
                                    isOpen
                                        ? 'rounded-t-xl bg-red-600 text-white hover:bg-red-600 focus-visible:ring-white'
                                        : 'rounded-xl bg-white hover:bg-gray-50 text-gray-800 focus-visible:ring-red-500'
                                }`}
                                onClick={() => toggle(i)}
                                aria-expanded={isOpen}
                            >
                                <span className="font-semibold pr-4">{item.q}</span>
                                <FontAwesomeIcon
                                    icon={isOpen ? 'chevron-up' : 'chevron-down'}
                                    className={`flex-shrink-0 ${isOpen ? 'text-white' : 'text-red-500'}`}
                                />
                            </button>
                            {isOpen && (
                                <div className="px-6 py-4 rounded-b-xl border-t border-gray-200 bg-gray-50">
                                    <p className="text-gray-700 leading-relaxed">{item.a}</p>
                                </div>
                            )}
                        </div>
                        );
                    })}
                </div>
                {/* JSON-LD for FAQPage — injected inline so it's always present */}
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'FAQPage',
                            mainEntity: FAQ_ITEMS.map(item => ({
                                '@type': 'Question',
                                name: item.q,
                                acceptedAnswer: { '@type': 'Answer', text: item.a }
                            }))
                        })
                    }}
                />
            </div>
        </section>
    );
};

const Home = () => {
    const [popularThemes, setPopularThemes] = useState([]);
    const [userCollections, setUserCollections] = useState([]);
    const [recentSets, setRecentSets] = useState([]);
    const [hasRecentSets, setHasRecentSets] = useState(false);
    const [selectedSets, setSelectedSets] = useState({});
    const [submittedSets, setSubmittedSets] = useState({});
    const [showAllRecentSets, setShowAllRecentSets] = useState(false);
    const [loadedImages, setLoadedImages] = useState({});
    const [loadedThemeImages, setLoadedThemeImages] = useState({});
    const [loadedFeaturedSetImage, setLoadedFeaturedSetImage] = useState(false);
    const [loadedBlogImages, setLoadedBlogImages] = useState({});
    const [loadedProfileImages, setLoadedProfileImages] = useState({});
    const [activeTab, setActiveTab] = useState('recent');
    const [featuredUserSet, setFeaturedUserSet] = useState(null);
    const [loadingFeatured, setLoadingFeatured] = useState(true);
    const [recentBlogPosts, setRecentBlogPosts] = useState([]);
    const [loadingBlogPosts, setLoadingBlogPosts] = useState(true);
    const navigate = useNavigate();
    const { user } = useAuth();

    useDocumentTitle(
        'Free LEGO® Collection Tracker & Price Guide',
        'Track, organize, and value your entire LEGO® collection for free. Browse 25,000+ sets, check retail and aftermarket prices for retired sets, manage your wishlist, and track minifigures. Join thousands of LEGO collectors on MyBrickLog.'
    );

    const [siteStats, setSiteStats] = useState({
        total_sets: 0,
        total_themes: 0,
        total_priced_sets: 0,
        loading: true
    });

    const INITIAL_SETS_TO_SHOW = 8;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [
                    themesResult,
                    collectionsResult,
                    recentSetsResult,
                    statsResult,
                    featuredUserSetResult,
                    blogPostsResult
                ] = await Promise.allSettled([
                    axios.get(`${process.env.REACT_APP_API_URL}/get_popular_themes.php`),
                    axios.get(`${process.env.REACT_APP_API_URL}/get_random_collections.php?public_only=1`),
                    axios.get(`${process.env.REACT_APP_API_URL}/get_recent_set_additions.php`),
                    axios.get(`${process.env.REACT_APP_API_URL}/get_site_statistics.php`),
                    axios.get(`${process.env.REACT_APP_API_URL}/get_random_user_set.php?public_only=1`),
                    axios.get(`${process.env.REACT_APP_API_URL}/blog_get_posts.php?limit=3`)
                ]);

                if (themesResult.status === 'fulfilled') {
                    setPopularThemes(themesResult.value.data);
                } else {
                    console.error('Failed to load popular themes:', themesResult.reason);
                }

                if (collectionsResult.status === 'fulfilled') {
                    setUserCollections(collectionsResult.value.data);
                } else {
                    console.error('Failed to load collections:', collectionsResult.reason);
                }

                if (recentSetsResult.status === 'fulfilled') {
                    setRecentSets(recentSetsResult.value.data.sets);
                    setHasRecentSets(recentSetsResult.value.data.hasRecentSets);
                } else {
                    console.error('Failed to load recent sets:', recentSetsResult.reason);
                }

                if (statsResult.status === 'fulfilled') {
                    setSiteStats({
                        total_sets: statsResult.value.data.total_sets,
                        total_themes: statsResult.value.data.total_themes,
                        total_priced_sets: statsResult.value.data.total_priced_sets,
                        loading: false
                    });
                } else {
                    console.error('Failed to load site statistics:', statsResult.reason);
                    setSiteStats(prevState => ({ ...prevState, loading: false }));
                }

                if (featuredUserSetResult.status === 'fulfilled') {
                    const featuredData = featuredUserSetResult.value.data;
                    if (featuredData && !featuredData.error) {
                        setFeaturedUserSet(featuredData);
                    }
                } else {
                    console.error('Failed to load featured user set:', featuredUserSetResult.reason);
                }
                setLoadingFeatured(false);

                if (blogPostsResult.status === 'fulfilled') {
                    const blogData = blogPostsResult.value.data;
                    if (blogData && blogData.success) {
                        setRecentBlogPosts(blogData.posts || []);
                    }
                } else {
                    console.error('Failed to load blog posts:', blogPostsResult.reason);
                }
                setLoadingBlogPosts(false);
                
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoadingFeatured(false);
                setLoadingBlogPosts(false);
                setSiteStats(prevState => ({...prevState, loading: false}));
            }
        };
    
        fetchData();
    }, []);

    useEffect(() => {
        if (featuredUserSet?.set?.set_num) {
            setLoadedFeaturedSetImage(false);
        }
    }, [featuredUserSet?.set?.set_num]);

    const handleThemeClick = (themeId) => {
        navigate(`/themes/${themeId}`);
    };

    const toggleSelectSet = (setNum) => {
        if (user) {
            setSelectedSets(prevSelectedSets =>
                prevSelectedSets[setNum]
                    ? { ...prevSelectedSets, [setNum]: undefined }
                    : { ...prevSelectedSets, [setNum]: 1 }
            );
        } else {
            navigate('/login');
        }
    };

    const handleQuantityChange = (setNum, quantity) => {
        setSelectedSets(prevSelectedSets => ({
            ...prevSelectedSets,
            [setNum]: quantity
        }));
    };

    const addToCollection = (setNum, quantity) => {
        const setToAdd = [{ setNum, quantity }];
    
        axios.post(`${process.env.REACT_APP_API_URL}/add_to_collection.php`, { sets: setToAdd })
            .then(response => {
                if (response.data.success) {
                    setSubmittedSets(prev => ({ ...prev, [setNum]: true }));
                    setSelectedSets(prev => {
                        const newSelected = { ...prev };
                        delete newSelected[setNum];
                        return newSelected;
                    });
                } else {
                    alert('Failed to add set to collection.');
                }
            })
            .catch(error => console.error('Error adding set to collection:', error));
    };

    const addToWishlist = (setNum) => {
        const setsToAdd = [{ setNum, quantity: selectedSets[setNum] }];
    
        axios.post(`${process.env.REACT_APP_API_URL}/add_to_wishlist.php`, { sets: setsToAdd })
            .then(response => {
                if (response.data.success) {
                    setSelectedSets(prev => {
                        const newSelected = { ...prev };
                        delete newSelected[setNum];
                        return newSelected;
                    });
                } else {
                    alert('Failed to add set to wishlist.');
                }
            })
            .catch(error => console.error('Error adding set to wishlist:', error));
    };

    const handleImageLoad = (setNum) => {
        setLoadedImages(prev => ({ ...prev, [setNum]: true }));
    };

    const handleThemeImageLoad = (themeId) => {
        setLoadedThemeImages(prev => ({ ...prev, [themeId]: true }));
    };

    const handleFeaturedSetImageLoad = () => {
        setLoadedFeaturedSetImage(true);
    };

    const handleBlogImageLoad = (postId) => {
        setLoadedBlogImages(prev => ({ ...prev, [postId]: true }));
    };

    const handleProfileImageLoad = (userId) => {
        setLoadedProfileImages(prev => ({ ...prev, [userId]: true }));
    };

    const displayedSets = showAllRecentSets ? recentSets : recentSets.slice(0, INITIAL_SETS_TO_SHOW);
    const hasMoreSets = recentSets.length > INITIAL_SETS_TO_SHOW;

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white overflow-hidden min-h-[80vh] flex items-center">
                {/* Background Elements */}
                <div className="absolute inset-0">
                    {/* Subtle grid pattern */}
                    <div className="absolute inset-0 opacity-10" style={{ 
                        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.8) 1px, transparent 0)`,
                        backgroundSize: '40px 40px'
                    }}></div>
                    {/* Geometric shapes */}
                    <img
                        src="/images/2x1-brick-yellow.png"
                        alt=""
                        aria-hidden="true"
                        className="absolute top-20 left-10 w-24 h-24 opacity-20 transform rotate-12 pointer-events-none select-none object-contain"
                        draggable="false"
                    />
                    <img
                        src="/images/1x1-brick-yellow.png"
                        alt=""
                        aria-hidden="true"
                        className="hidden md:block absolute top-40 right-20 w-16 h-16 opacity-15 transform -rotate-12 pointer-events-none select-none object-contain"
                        draggable="false"
                    />
                    <img
                        src="/images/1x1-brick-red.png"
                        alt=""
                        aria-hidden="true"
                        className="absolute bottom-20 right-1/3 w-12 h-12 opacity-20 transform rotate-45 pointer-events-none select-none object-contain"
                        draggable="false"
                    />
                    <img
                        src="/images/1x1-brick-yellow.png"
                        alt=""
                        aria-hidden="true"
                        className="absolute bottom-24 right-12 w-14 h-14 opacity-20 transform -rotate-12 pointer-events-none select-none object-contain"
                        draggable="false"
                    />
                    <img
                        src="/images/1x1-brick-red.png"
                        alt=""
                        aria-hidden="true"
                        className="absolute bottom-16 left-12 w-14 h-14 opacity-20 transform rotate-12 pointer-events-none select-none object-contain"
                        draggable="false"
                    />
                    <img
                        src="/images/2x1-brick-yellow.png"
                        alt=""
                        aria-hidden="true"
                        className="absolute left-2 top-[28%] w-16 h-16 opacity-20 transform -rotate-12 pointer-events-none select-none object-contain"
                        draggable="false"
                    />
                    <img
                        src="/images/1x1-brick-yellow.png"
                        alt=""
                        aria-hidden="true"
                        className="absolute top-10 left-1/2 -translate-x-1/2 w-12 h-12 opacity-20 transform rotate-12 pointer-events-none select-none object-contain"
                        draggable="false"
                    />
                </div>
                
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24 relative z-10 w-full">
                    <div className="flex flex-col lg:flex-row items-center lg:items-center gap-12 lg:gap-16">
                        {/* Content Section */}
                        <div className="lg:w-3/5 text-center lg:text-left">
                            <div className="mb-6">
                                <div className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm font-medium mb-6 backdrop-blur-sm border border-white border-opacity-30">
                                    <FontAwesomeIcon icon="star" className="mr-2 text-yellow-300" />
                                    The Ultimate LEGO® Collection Manager
                                </div>
                                <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-none">
                                    <span className="block text-white mb-2">Track Your</span>
                                    <span className="block bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-400 bg-clip-text text-transparent">
                                        LEGO<span className="text-2xl md:text-3xl lg:text-4xl align-top">®</span> Collection
                                    </span>
                                </h1>
                                <p className="text-xl md:text-2xl mb-4 text-red-50 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                                    The free LEGO® database with <strong className="text-yellow-300">25,000+ sets</strong>, retail &amp; aftermarket prices, minifigure tracking, and a powerful collection manager. All in one place.
                                </p>
                                <ul className="text-red-100 text-sm md:text-base mb-8 space-y-1 max-w-xl mx-auto lg:mx-0 text-left list-none">
                                    <li><FontAwesomeIcon icon="check" className="mr-2 text-yellow-400" />Never buy a duplicate set again</li>
                                    <li><FontAwesomeIcon icon="check" className="mr-2 text-yellow-400" />Check sealed &amp; used prices for retired LEGO® sets</li>
                                    <li><FontAwesomeIcon icon="check" className="mr-2 text-yellow-400" />Track minifigures, sealed copies, and build status</li>
                                    <li><FontAwesomeIcon icon="check" className="mr-2 text-yellow-400" />Browse every theme and subtheme ever released</li>
                                </ul>
                            </div>
                            
                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                {user ? (
                                    <>
                                        <Link 
                                            to={`/collection/${encodeURIComponent(user.username)}`}
                                            className="group px-8 py-4 bg-white text-red-600 rounded-2xl font-bold text-lg hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 flex items-center justify-center"
                                        >
                                            <FontAwesomeIcon icon="folder-open" className="mr-3 group-hover:scale-110 transition-transform" />
                                            My Collection
                                        </Link>
                                        <Link 
                                            to="/themes"
                                            className="group px-8 py-4 bg-gradient-to-r from-yellow-400 to-amber-400 text-red-800 rounded-2xl font-bold text-lg hover:from-yellow-300 hover:to-amber-300 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 flex items-center justify-center"
                                        >
                                            <FontAwesomeIcon icon="plus" className="mr-3 group-hover:scale-110 transition-transform" />
                                            Add Sets
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        <Link 
                                            to="/register"
                                            className="group px-8 py-4 bg-gradient-to-r from-yellow-400 to-amber-400 text-red-800 rounded-2xl font-bold text-lg hover:from-yellow-300 hover:to-amber-300 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 flex items-center justify-center"
                                        >
                                            <FontAwesomeIcon icon="user-plus" className="mr-3 group-hover:scale-110 transition-transform" />
                                            Start Free Today
                                        </Link>
                                        <Link 
                                            to="/login"
                                            className="group px-8 py-4 bg-white bg-opacity-20 text-white hover:text-white rounded-2xl font-bold text-lg hover:bg-opacity-30 transition-all duration-300 border-2 border-white border-opacity-30 hover:border-opacity-50 backdrop-blur-sm flex items-center justify-center"
                                        >
                                            <FontAwesomeIcon icon="user" className="mr-3 group-hover:scale-110 text-white transition-transform" />
                                            Sign In
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>
                        
                        {/* Featured Set Section */}
                        <div className="lg:w-2/5 relative">
                            {loadingFeatured ? (
                                <div className="relative group">
                                    <div className="bg-white p-8 rounded-3xl shadow-2xl border-4 border-white">
                                        <div className="text-center relative pt-2">
                                            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-44">
                                                <Skeleton height={36} borderRadius={9999} />
                                            </div>
                                            <div className="h-48 lg:h-56 w-full mb-6 mt-4 rounded-2xl overflow-hidden">
                                                <Skeleton height="100%" width="100%" />
                                            </div>
                                            <Skeleton height={28} className="mb-2" />
                                            <Skeleton height={28} width="70%" className="mx-auto mb-4" />
                                            <div className="flex justify-center gap-4 mb-4">
                                                <Skeleton width={88} height={28} borderRadius={9999} />
                                                <Skeleton width={64} height={28} borderRadius={9999} />
                                            </div>
                                            <Skeleton height={16} width="55%" className="mx-auto mb-6" />
                                            <Skeleton height={48} borderRadius={12} />
                                        </div>
                                    </div>
                                    <img
                                        src="/images/1x1-brick-yellow.png"
                                        alt=""
                                        aria-hidden="true"
                                        className="absolute -top-8 -left-8 w-16 h-16 transform rotate-12 opacity-80 pointer-events-none select-none object-contain"
                                        draggable="false"
                                    />
                                    <img
                                        src="/images/2x1-brick-red.png"
                                        alt=""
                                        aria-hidden="true"
                                        className="absolute -bottom-6 -right-6 w-20 h-20 transform -rotate-12 opacity-70 pointer-events-none select-none object-contain"
                                        draggable="false"
                                    />
                                    <img
                                        src="/images/1x1-brick-yellow.png"
                                        alt=""
                                        aria-hidden="true"
                                        className="absolute top-16 -right-4 w-8 h-8 transform rotate-45 opacity-60 pointer-events-none select-none object-contain"
                                        draggable="false"
                                    />
                                </div>
                            ) : featuredUserSet ? (
                                <div className="relative group">
                                    {/* Main card */}
                                    <div className="bg-white p-8 rounded-3xl shadow-2xl transform rotate-2 group-hover:rotate-0 transition-all duration-500 hover:scale-105 border-4 border-white">
                                        <div className="text-center">
                                            {/* Featured badge */}
                                            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                                <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                                                    Featured Set
                                                </div>
                                            </div>
                                            
                                            <div className="h-48 lg:h-56 w-full flex items-center justify-center mb-6 bg-white rounded-2xl relative overflow-hidden">
                                                {!loadedFeaturedSetImage && (
                                                    <div className="absolute inset-0">
                                                        <Skeleton height="100%" width="100%" />
                                                    </div>
                                                )}
                                                <img
                                                    src={featuredUserSet.set.img_url || '/images/lego_piece_questionmark.png'}
                                                    alt={featuredUserSet.set.name}
                                                    className={`h-full object-contain max-w-full ${loadedFeaturedSetImage ? 'opacity-100' : 'opacity-0'}`}
                                                    onError={(e) => e.target.src = '/images/lego_piece_questionmark.png'}
                                                    onLoad={handleFeaturedSetImageLoad}
                                                    style={{ transition: 'opacity 0.3s' }}
                                                />
                                            </div>
                                            
                                            <h3 className="text-2xl text-gray-800 font-bold text-center mb-3 line-clamp-2">
                                                {featuredUserSet.set.name}
                                            </h3>
                                            
                                            <div className="flex items-center justify-center gap-4 mb-4">
                                                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-semibold">
                                                    #{featuredUserSet.set.set_num}
                                                </span>
                                                <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                                                    {featuredUserSet.set.year}
                                                </span>
                                            </div>
                                            
                                            <p className="text-gray-600 mb-6 text-sm">
                                                from <Link 
                                                    to={`/collection/${encodeURIComponent(featuredUserSet.user.username)}`}
                                                    className="text-red-600 hover:text-red-800 font-bold underline decoration-2 underline-offset-2"
                                                >
                                                    {featuredUserSet.user.username}'s
                                                </Link> collection
                                            </p>
                                            
                                            <button 
                                                onClick={() => toggleSelectSet(featuredUserSet.set.set_num)}
                                                className="w-full px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-bold hover:from-red-700 hover:to-red-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                                            >
                                                <FontAwesomeIcon icon="plus" className="mr-2" />
                                                Add to My Collection
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* Floating decorative elements */}
                                    <img
                                        src="/images/1x1-brick-yellow.png"
                                        alt=""
                                        aria-hidden="true"
                                        className="absolute -top-8 -left-8 w-16 h-16 transform rotate-12 opacity-80 pointer-events-none select-none object-contain"
                                        draggable="false"
                                    />
                                    <img
                                        src="/images/2x1-brick-red.png"
                                        alt=""
                                        aria-hidden="true"
                                        className="absolute -bottom-6 -right-6 w-20 h-20 transform -rotate-12 opacity-70 pointer-events-none select-none object-contain"
                                        draggable="false"
                                    />
                                    <img
                                        src="/images/1x1-brick-yellow.png"
                                        alt=""
                                        aria-hidden="true"
                                        className="absolute top-16 -right-4 w-8 h-8 transform rotate-45 opacity-60 pointer-events-none select-none object-contain"
                                        draggable="false"
                                    />
                                </div>
                            ) : (
                                <div className="bg-white bg-opacity-20 backdrop-blur-sm p-8 rounded-3xl border border-white border-opacity-30">
                                    <div className="text-center">
                                        <FontAwesomeIcon icon="cubes" className="text-6xl mb-4 opacity-50" />
                                        <p className="text-white text-lg">No featured set available</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                {/* Content Selection Tabs */}
                <div className="flex justify-center mb-12">
                    <div className="flex gap-2 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-2 shadow-lg border border-gray-200 backdrop-blur-sm isolate">
                        <button
                            className={`relative px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-300 transform ${
                                activeTab === 'recent' 
                                    ? 'bg-red-600 text-white shadow-lg scale-105 z-20' 
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-white hover:shadow-md hover:scale-[1.02] hover:z-10'
                            }`}
                            onClick={() => setActiveTab('recent')}
                        >
                            <FontAwesomeIcon icon="clock" className="mr-2" />
                            Recent Sets
                        </button>
                        <button
                            className={`relative px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-300 transform ${
                                activeTab === 'themes' 
                                    ? 'bg-red-600 text-white shadow-lg scale-105 z-20' 
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-white hover:shadow-md hover:scale-[1.02] hover:z-10'
                            }`}
                            onClick={() => setActiveTab('themes')}
                        >
                            <FontAwesomeIcon icon="cubes" className="mr-2" />
                            Popular Themes
                        </button>
                        <button
                            className={`relative px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-300 transform ${
                                activeTab === 'users' 
                                    ? 'bg-red-600 text-white shadow-lg scale-105 z-20' 
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-white hover:shadow-md hover:scale-[1.02] hover:z-10'
                            }`}
                            onClick={() => setActiveTab('users')}
                        >
                            <FontAwesomeIcon icon="users" className="mr-2" />
                            User Collections
                        </button>
                    </div>
                </div>

                {/* Recently Added Sets */}
                {activeTab === 'recent' && (
                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                        <div className="bg-gradient-to-r from-red-600 to-red-700 p-6">
                            <div className="flex justify-between items-center">
                                <h2 className="text-2xl font-bold text-white flex items-center">
                                    <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                                        <FontAwesomeIcon icon="clock" className="text-white" />
                                    </div>
                                    Recently Added Sets
                                </h2>
                                <Link to="/themes" className="text-white hover:text-yellow-300 transition-colors text-sm font-medium bg-white bg-opacity-20 px-4 py-2 rounded-full">
                                    View All Sets <FontAwesomeIcon icon="arrow-right" className="ml-1" />
                                </Link>
                            </div>
                        </div>
                        <div className="p-6">
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                            {displayedSets.map(set => (
                                <div
                                    key={set.set_num}
                                    className={`rounded-lg shadow-sm border overflow-hidden transition-all duration-200 hover:shadow-md ${
                                        selectedSets[set.set_num] ? 'border-red-500 ring-2 ring-red-500 ring-opacity-50' : 'border-gray-200'
                                    }`}
                                >
                                    <div className="p-4">
                                                                <div className="h-32 flex items-center justify-center mb-4 relative">
                            {!loadedImages[set.set_num] && (
                                <div className="absolute inset-0">
                                    <Skeleton height="100%" width="100%" />
                                </div>
                            )}
                            <img
                                src={set.img_url || '/images/lego_piece_questionmark.png'}
                                alt={set.name}
                                className={`h-full object-contain max-w-full ${loadedImages[set.set_num] ? 'opacity-100' : 'opacity-0'}`}
                                onError={(e) => e.target.src = '/images/lego_piece_questionmark.png'}
                                onLoad={() => handleImageLoad(set.set_num)}
                                style={{ transition: 'opacity 0.3s' }}
                            />
                        </div>
                                        
                                        <h3 className="font-medium text-gray-800 mb-1 line-clamp-2 h-12">
                                            {set.name}
                                        </h3>
                                        
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="text-sm text-gray-600">{set.set_num}</span>
                                            <span className="text-sm font-semibold bg-gray-200 px-2 py-1 rounded">{set.year}</span>
                                        </div>
                                        
                                        {selectedSets[set.set_num] !== undefined ? (
                                            <div className="space-y-2">
                                                <div className="flex items-center justify-center gap-2 bg-gray-100 p-2 rounded">
                                                    <button
                                                        className="w-8 h-8 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleQuantityChange(set.set_num, Math.max(1, selectedSets[set.set_num] - 1));
                                                        }}
                                                    >
                                                        -
                                                    </button>
                                                    <input
                                                        type="text"
                                                        className="w-12 h-8 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-red-500"
                                                        value={selectedSets[set.set_num]}
                                                        onChange={(e) => {
                                                            const quantity = parseInt(e.target.value, 10);
                                                            if (!isNaN(quantity) && quantity > 0) {
                                                                handleQuantityChange(set.set_num, quantity);
                                                            }
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <button
                                                        className="w-8 h-8 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleQuantityChange(set.set_num, selectedSets[set.set_num] + 1);
                                                        }}
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                                <div className="flex flex-col gap-2">
                                                    <button
                                                        className={`py-2 px-3 rounded text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-500 ${
                                                            submittedSets[set.set_num] 
                                                                ? 'bg-green-600 hover:bg-green-700' 
                                                                : 'bg-red-600 hover:bg-red-700'
                                                        }`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            addToCollection(set.set_num, selectedSets[set.set_num]);
                                                        }}
                                                    >
                                                        {submittedSets[set.set_num] ? (
                                                            <>
                                                                <FontAwesomeIcon icon="check" className="mr-2" />
                                                                Added!
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FontAwesomeIcon icon="plus" className="mr-2" />
                                                                Add To Collection
                                                            </>
                                                        )}
                                                    </button>
                                                    <button
                                                        className="py-2 px-3 bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded text-sm font-medium focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            addToWishlist(set.set_num);
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon="heart" className="mr-2" />
                                                        Add to Wishlist
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => toggleSelectSet(set.set_num)}
                                                className="w-full py-2 px-3 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                                            >
                                                <FontAwesomeIcon icon="plus" className="mr-2" />
                                                Add Set
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        {hasMoreSets && (
                            <div className="text-center mt-8">
                                <button 
                                    className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                                    onClick={() => setShowAllRecentSets(!showAllRecentSets)}
                                >
                                    {showAllRecentSets ? (
                                        <>
                                            Show Less <FontAwesomeIcon icon="chevron-up" className="ml-2" />
                                        </>
                                    ) : (
                                        <>
                                            Show More <FontAwesomeIcon icon="chevron-down" className="ml-2" />
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                        </div>
                    </div>
                )}

                {/* Popular Themes */}
                {activeTab === 'themes' && (
                    <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-xl border border-blue-100 overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
                            <div className="flex justify-between items-center">
                                <h2 className="text-2xl font-bold text-white flex items-center">
                                    <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                                        <FontAwesomeIcon icon="cubes" className="text-white" />
                                    </div>
                                    Popular Themes
                                </h2>
                                <Link to="/themes" className="text-white hover:text-yellow-300 transition-colors text-sm font-medium bg-white bg-opacity-20 px-4 py-2 rounded-full">
                                    View All Themes <FontAwesomeIcon icon="arrow-right" className="ml-1" />
                                </Link>
                            </div>
                        </div>
                        <div className="p-6">
                        
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {popularThemes.map(theme => (
                                <div 
                                    key={theme.id} 
                                    className="bg-gray-50 rounded-lg overflow-hidden shadow-sm border border-gray-200 hover:shadow-md cursor-pointer transition-all duration-200 hover:-translate-y-1"
                                    onClick={() => handleThemeClick(theme.id)}
                                >
                                    <div className="h-32 relative bg-white p-2">
                                        {!loadedThemeImages[theme.id] && (
                                            <div className="absolute inset-0">
                                                <Skeleton height="100%" width="100%" />
                                            </div>
                                        )}
                                        <img 
                                            src={themeImageUrl(theme.id)}
                                            alt={theme.name} 
                                            className={`w-full h-full object-contain object-center ${loadedThemeImages[theme.id] ? 'opacity-100' : 'opacity-0'}`}
                                            onError={(e) => e.target.src = '/images/lego_piece_questionmark.png'}
                                            onLoad={() => handleThemeImageLoad(theme.id)}
                                            style={{ transition: 'opacity 0.3s' }}
                                        />
                                        
                                    </div>
                                    <div className="p-3">
                                        <h3 className="font-semibold text-gray-800 truncate">{theme.name}</h3>
                                        <p className="text-xs text-gray-500">
                                            {theme.collection_count} sets in collections
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        </div>
                    </div>
                )}

                {/* User Collections */}
                {activeTab === 'users' && (
                    <div className="bg-gradient-to-br from-white to-purple-50 rounded-2xl shadow-xl border border-purple-100 overflow-hidden">
                        <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6">
                            <div className="flex justify-between items-center">
                                <h2 className="text-2xl font-bold text-white flex items-center">
                                    <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                                        <FontAwesomeIcon icon="users" className="text-white" />
                                    </div>
                                    User Collections
                                </h2>
                                {user && (
                                    <Link to={`/collection/${encodeURIComponent(user.username)}`} className="text-white hover:text-yellow-300 transition-colors text-sm font-medium bg-white bg-opacity-20 px-4 py-2 rounded-full">
                                        View My Collection <FontAwesomeIcon icon="arrow-right" className="ml-1" />
                                    </Link>
                                )}
                            </div>
                        </div>
                        <div className="p-6">
                        
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            {userCollections.map(user => (
                                <Link 
                                    key={user.user_id} 
                                    to={`/collection/${encodeURIComponent(user.username)}`} 
                                    className="block group"
                                >
                                    <div className="bg-gray-50 rounded-lg overflow-hidden shadow-sm border border-gray-200 group-hover:shadow-md transition-all duration-200 group-hover:-translate-y-1">
                                        <div className="aspect-square overflow-hidden relative bg-gray-100">
                                            {!loadedProfileImages[user.user_id] && (
                                                <div className="absolute inset-0">
                                                    <Skeleton height="100%" width="100%" />
                                                </div>
                                            )}
                                            <img 
                                                src={profilePictureUrl(user.profile_picture) || '/images/lego_user.png'}
                                                alt={user.username} 
                                                className={`w-full h-full object-cover transition-opacity transition-transform duration-300 group-hover:scale-105 ${loadedProfileImages[user.user_id] ? 'opacity-100' : 'opacity-0'}`}
                                                onLoad={() => handleProfileImageLoad(user.user_id)}
                                            />
                                        </div>
                                        <div className="p-3 text-center">
                                            <h3 className="font-medium text-gray-800 truncate">{user.username}</h3>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Statistics Banner */}
            <section className="bg-gray-900 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                        <div className="p-6">
                            {siteStats.loading ? (
                                <div className="flex flex-col items-center justify-center gap-2">
                                    <Skeleton width={140} height={40} baseColor="#374151" highlightColor="#4b5563" />
                                    <Skeleton width={200} height={18} baseColor="#374151" highlightColor="#4b5563" />
                                </div>
                            ) : (
                                <>
                                    <div className="text-4xl font-bold text-yellow-400 mb-2">{siteStats.total_sets.toLocaleString()}+</div>
                                    <p className="text-gray-300">LEGO<span className="text-xs align-top">®</span> Sets Cataloged</p>
                                </>
                            )}
                        </div>
                        <div className="p-6">
                            {siteStats.loading ? (
                                <div className="flex flex-col items-center justify-center gap-2">
                                    <Skeleton width={120} height={40} baseColor="#374151" highlightColor="#4b5563" />
                                    <Skeleton width={160} height={18} baseColor="#374151" highlightColor="#4b5563" />
                                </div>
                            ) : (
                                <>
                                    <div className="text-4xl font-bold text-yellow-400 mb-2">{siteStats.total_themes.toLocaleString()}+</div>
                                    <p className="text-gray-300">Unique Themes</p>
                                </>
                            )}
                        </div>
                        <div className="p-6">
                            {siteStats.loading ? (
                                <div className="flex flex-col items-center justify-center gap-2">
                                    <Skeleton width={140} height={40} baseColor="#374151" highlightColor="#4b5563" />
                                    <Skeleton width={180} height={18} baseColor="#374151" highlightColor="#4b5563" />
                                </div>
                            ) : (
                                <>
                                    <div className="text-4xl font-bold text-yellow-400 mb-2">{siteStats.total_priced_sets.toLocaleString()}+</div>
                                    <p className="text-gray-300">Sets Priced</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Recent Blog Posts Section */}
            {(loadingBlogPosts || recentBlogPosts.length > 0) && (
                <section className="py-12 bg-gradient-to-br from-gray-50 to-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6">
                        <div className="text-center mb-8">
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                                <FontAwesomeIcon icon="newspaper" className="mr-3 text-red-600" />
                                Latest from the Blog
                            </h2>
                            <p className="text-gray-600 max-w-2xl mx-auto">
                                Stay updated with the latest LEGO® news, reviews, and collection tips from our community.
                            </p>
                        </div>
                        
                        <div className={`grid gap-6 mb-8 ${
                            loadingBlogPosts
                                ? 'grid-cols-1 md:grid-cols-3'
                                : recentBlogPosts.length === 1
                                ? 'grid-cols-1 max-w-md mx-auto'
                                : recentBlogPosts.length === 2
                                ? 'grid-cols-1 md:grid-cols-2 max-w-2xl mx-auto'
                                : 'grid-cols-1 md:grid-cols-3'
                        }`}>
                            {loadingBlogPosts ? (
                                [0, 1, 2].map((i) => (
                                    <div
                                        key={i}
                                        className="block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                                    >
                                        <div className="aspect-[4/3] relative bg-gray-100">
                                            <div className="absolute inset-0">
                                                <Skeleton height="100%" width="100%" />
                                            </div>
                                        </div>
                                        <div className="p-4">
                                            <Skeleton width={72} height={22} borderRadius={9999} className="mb-3" />
                                            <Skeleton height={20} className="mb-2" />
                                            <Skeleton height={20} width="85%" className="mb-3" />
                                            <Skeleton height={14} className="mb-2" />
                                            <Skeleton height={14} width="70%" />
                                            <div className="flex justify-between mt-4">
                                                <Skeleton width={96} height={14} />
                                                <Skeleton width={56} height={14} />
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                recentBlogPosts.map((post) => (
                                    <Link
                                        key={post.id}
                                        to={`/blog/${post.slug}`}
                                        className="group block bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 overflow-hidden hover:-translate-y-1"
                                    >
                                        <div className="aspect-[4/3] overflow-hidden bg-gray-100 relative">
                                            {!loadedBlogImages[post.id] && (
                                                <div className="absolute inset-0">
                                                    <Skeleton height="100%" width="100%" />
                                                </div>
                                            )}
                                            <img
                                                src={post.featured_image || '/images/lego_piece_questionmark.png'}
                                                alt={post.title}
                                                className={`w-full h-full object-cover object-center group-hover:scale-105 transition-opacity transition-transform duration-300 ${loadedBlogImages[post.id] ? 'opacity-100' : 'opacity-0'}`}
                                                onError={(e) => {
                                                    e.target.src = '/images/lego_piece_questionmark.png';
                                                }}
                                                onLoad={() => handleBlogImageLoad(post.id)}
                                            />
                                        </div>

                                        <div className="p-4">
                                            <div className="mb-2">
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                    {post.category?.name || 'General'}
                                                </span>
                                            </div>

                                            <h3 className="font-semibold text-gray-900 group-hover:text-red-600 transition-colors mb-2 line-clamp-2">
                                                {post.title}
                                            </h3>

                                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                                {post.excerpt}
                                            </p>

                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                <span>
                                                    <FontAwesomeIcon icon="user" className="mr-1" />
                                                    {post.author?.username || 'Admin'}
                                                </span>
                                                <span>
                                                    {new Date(post.published_at).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric'
                                                    })}
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                ))
                            )}
                        </div>

                        {!loadingBlogPosts && recentBlogPosts.length > 0 && (
                            <div className="text-center">
                                <Link
                                    to="/blog"
                                    className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 hover:text-white transition-colors font-medium shadow-md hover:shadow-lg"
                                >
                                    <FontAwesomeIcon icon="newspaper" className="mr-2" />
                                    View All Posts
                                    <FontAwesomeIcon icon="arrow-right" className="ml-2" />
                                </Link>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Features Section */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                            <span>Why Use</span>
                            <img
                                src="/images/mybricklog_logo_text.png"
                                alt="MyBrickLog"
                                className="h-8 sm:h-9 md:h-10 w-auto max-w-[min(70vw,280px)] object-contain"
                            />
                            <span>?</span>
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                            Everything a serious LEGO® collector needs. Completely free, forever.
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="bg-gray-50 p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-red-100 text-red-600 rounded-xl flex items-center justify-center mb-4">
                                <FontAwesomeIcon icon="folder-open" size="lg" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">Organize Your Entire Collection</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Log every LEGO® set you own with quantity tracking. Mark individual copies as <strong>sealed</strong>, <strong>opened</strong>, or <strong>complete</strong>. Sort and filter your collection by theme, year, piece count, or value at a glance; always know exactly what you have.
                            </p>
                        </div>
                        
                        <div className="bg-gray-50 p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-yellow-100 text-yellow-600 rounded-xl flex items-center justify-center mb-4">
                                <FontAwesomeIcon icon="heart" size="lg" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">Build &amp; Share Wishlists</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Add sets you want to a personal wishlist before you buy. Share your wishlist with family before birthdays or holidays so they know exactly which sets to get. Never receive a duplicate gift again.
                            </p>
                        </div>
                        
                        <div className="bg-gray-50 p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-green-100 text-green-600 rounded-xl flex items-center justify-center mb-4">
                                <FontAwesomeIcon icon="dollar-sign" size="lg" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">LEGO® Price Guide &amp; Retired Set Values</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Every set includes its original retail price. For <strong>retired LEGO® sets</strong>, we track current sealed and used aftermarket prices so you always know what your collection is worth, or whether now is the right time to buy.
                            </p>
                        </div>
                        
                        <div className="bg-gray-50 p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center mb-4">
                                <FontAwesomeIcon icon="puzzle-piece" size="lg" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">Minifigure Tracking</h3>
                            <p className="text-gray-600 leading-relaxed">
                                For every set in your collection, see the full list of included minifigures and mark which ones you have. Perfect for collectors who trade or hunt for specific minifigures across sets and themes.
                            </p>
                        </div>
                        
                        <div className="bg-gray-50 p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center mb-4">
                                <FontAwesomeIcon icon="cubes" size="lg" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">Complete LEGO® Set Database</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Browse <strong>25,000+ LEGO® sets</strong> across every theme ever released; from classic Space and Castle to modern Technic, Creator Expert, Star Wars™, Harry Potter™, and more. Every subtheme, every year, all in one searchable database.
                            </p>
                        </div>
                        
                        <div className="bg-gray-50 p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center mb-4">
                                <FontAwesomeIcon icon="users" size="lg" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">Community Collections</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Make your collection public and show it off to other LEGO® fans. Discover what fellow collectors own, get inspired by curated collections, and see which sets are the most popular among the MyBrickLog community.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-16 bg-gray-50">
                <div className="max-w-5xl mx-auto px-4 sm:px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            How It Works
                        </h2>
                        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                            Get your LEGO® collection organized in minutes.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-red-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl font-black shadow-lg">
                                1
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 mb-2">Create an Account</h3>
                            <p className="text-gray-600">
                                Sign up in seconds with just an email address. Your account is completely free.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-red-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl font-black shadow-lg">
                                2
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 mb-2">Search &amp; Add Your Sets</h3>
                            <p className="text-gray-600">
                                Browse by theme or search by set name or number. Add sets to your collection with one click, setting quantity and condition for each.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-red-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl font-black shadow-lg">
                                3
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 mb-2">Manage &amp; Explore</h3>
                            <p className="text-gray-600">
                                View your collection in grid or list view, check set values, track minifigures, build your wishlist, and share your collection with others.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <FaqSection />

            {/* Call to Action */}
            <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
                    <h2 className="text-3xl font-bold mb-4">Ready to organize your collection?</h2>
                    <p className="text-xl mb-8">Join thousands of LEGO® enthusiasts and take control of your brick collection today.</p>
                    
                    {!user ? (
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <Link 
                                to="/register"
                                className="px-8 py-3 bg-yellow-400 text-red-700 rounded-lg font-semibold hover:bg-yellow-300 transition-colors shadow-md text-lg"
                            >
                                <FontAwesomeIcon icon="user-plus" className="mr-2" />
                                Create Free Account
                            </Link>
                            <Link 
                                to="/login"
                                className="px-8 py-3 bg-white bg-opacity-20 text-white hover:text-white rounded-lg font-semibold hover:bg-opacity-30 transition-colors border border-white border-opacity-50 text-lg"
                            >
                                <FontAwesomeIcon icon="user" className="mr-2" />
                                Sign In
                            </Link>
                        </div>
                    ) : (
                        <div className="flex justify-center">
                            <Link 
                                to="/themes"
                                className="px-8 py-3 bg-yellow-400 text-red-700 rounded-lg font-semibold hover:bg-yellow-300 transition-colors shadow-md text-lg"
                            >
                                <FontAwesomeIcon icon="plus" className="mr-2" />
                                Add Sets to Collection
                            </Link>
                        </div>
                    )}
                </div>
            </section>

            {/* Testimonials 
            <section className="py-16 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
                        What Builders Are Saying
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white p-6 rounded-lg shadow relative">
                            <div className="text-yellow-400 mb-4 flex">
                                <FontAwesomeIcon icon="star" />
                                <FontAwesomeIcon icon="star" />
                                <FontAwesomeIcon icon="star" />
                                <FontAwesomeIcon icon="star" />
                                <FontAwesomeIcon icon="star" />
                            </div>
                            <p className="text-gray-600 mb-4">
                                "Finally, a way to keep track of my massive collection! I used to buy duplicates all the time, but not anymore."
                            </p>
                            <div className="flex items-center">
                                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold mr-3">
                                    M
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-800">Mark Thompson</h4>
                                    <p className="text-sm text-gray-500">LEGO City collector</p>
                                </div>
                            </div>
                            <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded transform rotate-12"></div>
                        </div>
                        
                        <div className="bg-white p-6 rounded-lg shadow relative">
                            <div className="text-yellow-400 mb-4 flex">
                                <FontAwesomeIcon icon="star" />
                                <FontAwesomeIcon icon="star" />
                                <FontAwesomeIcon icon="star" />
                                <FontAwesomeIcon icon="star" />
                                <FontAwesomeIcon icon="star" />
                            </div>
                            <p className="text-gray-600 mb-4">
                                "I love being able to showcase my collection and seeing what other builders have. Great community!"
                            </p>
                            <div className="flex items-center">
                                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold mr-3">
                                    S
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-800">Sarah Johnson</h4>
                                    <p className="text-sm text-gray-500">Star Wars enthusiast</p>
                                </div>
                            </div>
                            <div className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 rounded transform rotate-12"></div>
                        </div>
                        
                        <div className="bg-white p-6 rounded-lg shadow relative">
                            <div className="text-yellow-400 mb-4 flex">
                                <FontAwesomeIcon icon="star" />
                                <FontAwesomeIcon icon="star" />
                                <FontAwesomeIcon icon="star" />
                                <FontAwesomeIcon icon="star" />
                                <FontAwesomeIcon icon="star" />
                            </div>
                            <p className="text-gray-600 mb-4">
                                "The wishlist feature helps me plan my future purchases. This site has become essential for my LEGO hobby."
                            </p>
                            <div className="flex items-center">
                                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold mr-3">
                                    J
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-800">James Rodriguez</h4>
                                    <p className="text-sm text-gray-500">Technic builder</p>
                                </div>
                            </div>
                            <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded transform rotate-12"></div>
                        </div>
                    </div>
                </div>
            </section>*/}

            {/* Newsletter */}
            <section className="py-12 bg-white border-t border-gray-200">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Stay Updated with LEGO® News</h2>
                    <p className="text-gray-600 mb-6">
                        Subscribe to our newsletter for the latest set releases, features, and community highlights.
                    </p>
                    
                    <NewsletterForm />
                    
                    <p className="text-xs text-gray-500 mt-4">
                        We respect your privacy and will never share your information.
                    </p>
                </div>
            </section>
        </div>
    );
};

export default Home;